"use client";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "@commerce-frontend/ui/components/Dialog/Dialog";
import type { CompanyLinks } from "@commerce-frontend/ui/providers/StaticSiteConfigProvider";
import type { Locale, SupportedCountries } from "@commerce-shared/site-config";
import { getSupportedLocalesWithSiteConfig } from "@commerce-shared/site-config";
import type { FragmentOf } from "gql.tada";
import { useTranslations } from "next-intl";
import { useState } from "react";
import type { AccountMenuItem } from "../../../account/_components/layout/types";
import type {
	MenuItemFragment,
	SubMenuItemFragment,
} from "../Header.fragments";
import { LanguageSelect } from "./LanguageSelect";
import { MainMenu } from "./MainMenu";
import { SubMenu } from "./SubMenu";

type Props = {
	topLevelMenuItems: FragmentOf<typeof MenuItemFragment>[];
	staticLinks: CompanyLinks;
	accountLinks: AccountMenuItem[];
	locale: Locale;
	supportedCountries: SupportedCountries;
};

export const MobileMainMenu = ({
	topLevelMenuItems,
	staticLinks,
	accountLinks,
	locale,
	supportedCountries,
}: Props) => {
	const t = useTranslations("Navigation");

	const [selectedItem, setSelectedItem] = useState<
		FragmentOf<typeof MenuItemFragment> | FragmentOf<typeof SubMenuItemFragment>
	>();
	const [languageOpen, setLanguageOpen] = useState(false);
	const localesWithsStaticSiteConfig =
		getSupportedLocalesWithSiteConfig(supportedCountries);

	const parentItem =
		selectedItem?.__typename === "SubMenuItem"
			? topLevelMenuItems.find((item) =>
					item.subMenuItems?.includes(selectedItem),
				)
			: undefined;

	return (
		<Dialog closeOnInteractOutside={false} closeOnPathnameChange>
			<DialogTrigger
				aria-label={t("menuOpenMenu")}
				className="hover:text -m-1 flex rounded p-1 hover:bg-blue-400 lg:hidden"
			>
				<Icon icon="menu-alt-4" className="bg-current" />
			</DialogTrigger>

			<DialogContent
				side="left"
				className="flex flex-col"
				aria-label={t("menuAccessibilityName")}
			>
				<DialogTitle className="sr-only">
					{t("menuAccessibilityName")}
				</DialogTitle>
				{selectedItem && !languageOpen && (
					<SubMenu
						menuItem={selectedItem}
						onSubMenuItemSelect={setSelectedItem}
						onBack={() => setSelectedItem(parentItem)}
						parentItem={parentItem}
					/>
				)}
				{!selectedItem && languageOpen && (
					<LanguageSelect
						onBack={() => setLanguageOpen(false)}
						locale={locale}
						localesWithsStaticSiteConfig={localesWithsStaticSiteConfig}
					/>
				)}
				{!selectedItem && !languageOpen && (
					<MainMenu
						topLevelMenuItems={topLevelMenuItems}
						onMainMenuItemSelect={setSelectedItem}
						onLanguageSelect={setLanguageOpen}
						staticLinks={staticLinks}
						accountLinks={accountLinks}
						locale={locale}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
};

export const MobileMainMenuIntegration = (props: Omit<Props, "pathname">) => (
	<MobileMainMenu {...props} />
);
